import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { List, ListItem, SkeletonLoader } from "@activecollab/components";
import PropTypes from "prop-types";
import { makeGetAllAttachmentsByParent } from "~/store/selectors/selector.attachments";
import { isGoogleDriveFile as getIsGoogleDriveFile, isDropboxFile as getIsDropboxFile } from "~/utils/files.jsx";
import { descSortCompare, prepareThumbnailUrl } from "~/utils/utils";
import { ExpenseAttachmentWrapper } from "./ExpenseAttachmentWrapper";
import { useFilePreviewContext, useFileTokenContext } from "../FilePreview";
import { SelectAttachmentsItem } from "../select_attachments/SelectAttachmentsItem";
import { UploadedFileAsListItemContainer } from "../select_attachments/UploadedFileAsListItemContainer";
import { DropboxAttachmentIcon, GoogleDriveAttachmentIcon } from "../svg";

export const AttachmentList = ({
  parentType,
  parentId,
  onDelete,
  onRetry,
  onRemove,
  uploadsList,
  filesToAttach,
  canEdit,
}) => {
  const isGoogleDriveFile = useCallback((type) => getIsGoogleDriveFile(type), []);
  const isDropboxFile = useCallback((type) => getIsDropboxFile(type), []);

  const getAllObjectAttachments = useMemo(() => {
    return makeGetAllAttachmentsByParent({
      parentType,
      parentId,
    });
  }, [parentType, parentId]);

  const attachments = useSelector(getAllObjectAttachments);

  const sortedAttachments = useMemo(() => {
    return attachments.length ? attachments.sort((a, b) => descSortCompare(a.created_on, b.created_on)) : [];
  }, [attachments]);

  const { openFilePreview } = useFilePreviewContext();
  const { download_token, thumb_token } = useFileTokenContext();

  const handleOpenFilePreview = useCallback(
    (id) => {
      openFilePreview(id, attachments);
    },
    [attachments, openFilePreview]
  );

  const thumbnailUrl = useCallback(
    (thumbnail) => prepareThumbnailUrl(thumbnail, 60, 60, "crop", thumb_token, download_token),
    [download_token, thumb_token]
  );

  const uniqueAttachments = useMemo(() => {
    if (!filesToAttach.length) return sortedAttachments;

    const codeMap = new Map();
    const filesArray = [];

    sortedAttachments.forEach((file) => {
      if (file.code) {
        codeMap.set(file.code, true);
      }
    });

    filesToAttach.forEach((file) => {
      if (!codeMap.has(file.code)) {
        filesArray.push(file);
      }
    });

    return [...filesArray, ...sortedAttachments];
  }, [filesToAttach, sortedAttachments]);

  const showList = attachments.length || uploadsList.length || filesToAttach.length;

  return (
    <List className="tw-py-3">
      {showList ? (
        <div className="tw-max-h-[304px] tw-overflow-auto">
          {uploadsList.map((u) => (
            <ExpenseAttachmentWrapper
              key={u.code}
              id={u.code}
              name={u.name}
              onRemove={u.error ? onRemove : undefined}
              canEdit={canEdit}
            >
              <SelectAttachmentsItem
                id={u.code}
                name={u.name}
                type="local"
                uploading={u.uploading}
                progress={u.progress}
                errorMessage={u.errorMessage}
                error={u.error}
                size={48}
                onRetry={onRetry}
              />
            </ExpenseAttachmentWrapper>
          ))}
          {uniqueAttachments.map((attachment, index) => {
            if (!attachment?.id)
              return (
                <ExpenseAttachmentWrapper
                  id={attachment.code}
                  name={attachment.name}
                  extension={attachment.extension}
                  sizeValue={attachment.size}
                  canEdit={canEdit}
                  key={index}
                >
                  <UploadedFileAsListItemContainer
                    id={attachment.code}
                    size={48}
                    disableTooltip
                  />
                </ExpenseAttachmentWrapper>
              );
            return (
              <ExpenseAttachmentWrapper
                key={index}
                id={attachment.id}
                name={attachment.name}
                extension={attachment.extension}
                onOpenPreview={handleOpenFilePreview}
                createdById={attachment.created_by_id}
                onDelete={onDelete}
                sizeValue={attachment.sizeValue}
                canEdit={canEdit}
              >
                <div className="ac-border-radius-4 tw-w-full tw-h-full tw-overflow-hidden tw-flex tw-items-center tw-justify-center">
                  {isGoogleDriveFile(attachment.class) ? <GoogleDriveAttachmentIcon /> : null}
                  {isDropboxFile(attachment.class) ? <DropboxAttachmentIcon /> : null}
                  {!isGoogleDriveFile(attachment.class) && !isDropboxFile(attachment.class) ? (
                    <img
                      src={thumbnailUrl(attachment.thumbnail_url)}
                      alt={attachment.name}
                      className="tw-h-full tw-w-full tw-object-cover tw-text-transparent"
                    />
                  ) : null}
                </div>
              </ExpenseAttachmentWrapper>
            );
          })}
        </div>
      ) : (
        <ListItem className="hover:tw-bg-transparent tw-py-1">
          <SkeletonLoader className="tw-h-12 tw-w-full" />
        </ListItem>
      )}
    </List>
  );
};

AttachmentList.propTypes = {
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
  onDelete: PropTypes.func,
  onRetry: PropTypes.func,
  onRemove: PropTypes.func,
  uploadsList: PropTypes.array,
  filesToAttach: PropTypes.array,
  canEdit: PropTypes.bool,
};
AttachmentList.defaultProps = {
  uploadsList: [],
  filesToAttach: [],
};
